import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Logo from "./Logo";
import { LogOut, LayoutDashboard, Crown, Briefcase, Menu, X, ShieldCheck } from "lucide-react";

export default function Navbar({ variant = "marketing" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  const navLinks = [
    { to: "/parent", label: "Parent Co" },
    { to: "/dmcs", label: "Premium DMCs" },
    { to: "/#how", label: "How it works" },
    { to: "/#pillars", label: "Why Join" },
  ];

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-hairline"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-10">
          <Logo size="md" />
          <nav className="hidden md:flex items-center gap-7 text-sm text-muted">
            {navLinks.map((l) => {
              const isAnchor = l.to.startsWith("/#") || l.to.startsWith("#");
              return isAnchor ? (
                <a
                  key={l.to}
                  href={l.to}
                  className="hover:text-ink transition-colors"
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.to}
                  to={l.to}
                  className="hover:text-ink transition-colors"
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="btn-ghost text-sm" data-testid="nav-admin-btn">
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link to="/dashboard" className="btn-ghost text-sm" data-testid="nav-dashboard-btn">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <button onClick={onLogout} className="btn-ghost text-sm" data-testid="nav-logout-btn">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost text-sm" data-testid="nav-login-btn">
                Sign in
              </Link>
              <Link to="/register" className="btn-primary text-sm" data-testid="nav-register-btn">
                <Briefcase className="w-4 h-4" /> Join the network
              </Link>
            </>
          )}
        </div>
        <button
          className="md:hidden text-ink"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-hairline px-6 py-4 flex flex-col gap-3 bg-white">
          {navLinks.map((l) => {
            const isAnchor = l.to.startsWith("/#") || l.to.startsWith("#");
            return isAnchor ? (
              <a key={l.to} href={l.to} onClick={() => setOpen(false)} className="text-[#a3a3a3] text-sm">
                {l.label}
              </a>
            ) : (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-[#a3a3a3] text-sm">
                {l.label}
              </Link>
            );
          })}
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} className="btn-ghost text-sm justify-center">Admin</Link>
              )}
              <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-ghost text-sm justify-center">Dashboard</Link>
              <button onClick={onLogout} className="btn-ghost text-sm justify-center">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost text-sm justify-center">Sign in</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="btn-primary text-sm justify-center">Join</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
